<script setup>
import { ref, defineProps, defineEmits, defineExpose } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import fetchData from "@/utils/fetchData";
import { count } from "@/utils/format";

const emit = defineEmits(["currentPageUpdate"]);

const props = defineProps({
  paginationUrl: {
    type: String,
    required: true,
  },
  // What the results are, for "129 pages of 1,287 journeys"
  noun: {
    type: String,
    default: "results",
  },
});

const currentPage = ref(1);
const totalPages = ref(1);
const totalResults = ref(0);
const pages = ref(
  Array.from(
    new Set([
      currentPage.value - 2,
      currentPage.value - 1,
      currentPage.value,
      currentPage.value + 1,
      currentPage.value + 2,
    ])
  ).filter((page) => page > 0 && page <= totalPages.value)
);
const firstJump = ref(pages.value[0] !== 1);
const lastJump = ref(pages.value[pages.value.length - 1] !== totalPages.value);

const getPages = () => {
  pages.value = Array.from(
    new Set([
      currentPage.value - 2,
      currentPage.value - 1,
      currentPage.value,
      currentPage.value + 1,
      currentPage.value + 2,
    ])
  ).filter((page) => page > 0 && page <= totalPages.value);
  firstJump.value = pages.value[0] !== 1;
  lastJump.value = pages.value[pages.value.length - 1] !== totalPages.value;
};

const setPage = (page) => {
  currentPage.value = page;
  emit("currentPageUpdate", page);
  getPages();
};

const loadTotalPages = async (search = {}) => {
  const result = await fetchData(props.paginationUrl, {
    search,
  });
  totalPages.value = result.totalPages;
  totalResults.value = result.totalResults;
  getPages();
};

defineExpose({
  loadTotalPages,
});
loadTotalPages();
</script>

<template>
  <nav
    class="mt-4 flex flex-wrap items-center justify-between gap-3"
    aria-label="Pages"
  >
    <p class="card-note">
      Page {{ currentPage }} of {{ count(totalPages) }},
      {{ count(totalResults) }} {{ noun }}
    </p>
    <div class="flex flex-wrap gap-1">
      <button
        v-if="firstJump"
        type="button"
        class="page-btn"
        title="First page"
        @click="setPage(1)"
      >
        <FontAwesomeIcon icon="angles-left" />
      </button>
      <button
        v-if="currentPage !== 1"
        type="button"
        class="page-btn"
        title="Previous page"
        @click="setPage(currentPage - 1)"
      >
        <FontAwesomeIcon icon="angle-left" />
      </button>
      <button
        v-for="page in pages"
        :key="page"
        type="button"
        class="page-btn"
        :class="{
          '!bg-primary !text-white dark:!bg-white dark:!text-primary':
            page === currentPage,
        }"
        :aria-current="page === currentPage ? 'page' : undefined"
        @click="setPage(page)"
      >
        {{ page }}
      </button>
      <button
        v-if="currentPage !== totalPages"
        type="button"
        class="page-btn"
        title="Next page"
        @click="setPage(currentPage + 1)"
      >
        <FontAwesomeIcon icon="angle-right" />
      </button>
      <button
        v-if="lastJump"
        type="button"
        class="page-btn"
        title="Last page"
        @click="setPage(totalPages)"
      >
        <FontAwesomeIcon icon="angles-right" />
      </button>
    </div>
  </nav>
</template>

<style scoped>
.page-btn {
  @apply flex h-9 min-w-[2.25rem] items-center justify-center rounded-lg px-2 text-sm font-semibold text-secondary transition-colors hover:bg-primary/5 hover:text-primary dark:text-night-muted dark:hover:bg-white/10 dark:hover:text-white;
}
</style>
